import { Modal } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import moment from 'moment';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Feedback,
  Input,
  Paragrapgh,
  Subtitle,
} from '../../shared';
import {
  IFarmResource,
  IMusselLineResource,
} from '../../../entities/farms.entities';
import {
  SelectFarmsByFarmID,
  SelectUtilsByFarmID,
  SelectIsBudgetVisible,
  SelectDetailedBudgetEnabled,
  SelectHarvestFields,
} from '../../../store/extra/extra.selector';
import {
  divideEqually,
  numToStr,
  toMillisecond,
  toSecond,
  getUnitOfSpecie,
  isNullOrUndefined,
} from '../../../lib/common.helpers';
import {
  selectAccount,
  selectProfile,
} from '../../../store/auth/auth.selector';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate } from '../../../lib/lang.helper';
import ExpensesForm from '../../grower-views/ExpensesForm';
import MoreFieldsForm from '../HarvestCompleteModal/MoreFieldsForm';
import HarvestBatchSelect, {
  HarvestGroupLensForm,
} from '../HarvestCompleteModal/HarvestBatchSelect';
import { showFeedback } from '../../../store/ui/ui.actions';
import { sendSingleRequest } from '../../../apis';
import {
  loadFarmsData,
  loadQueueData,
} from '../../../store/farms/farms.actions';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { IExpenseRequest } from '../../../entities/budget.entities';
import HarvestBagsForm from '../HarvestCompleteModal/HarvestBagsForm';
import { ICustomValue } from '../../../entities/automation.entities';
import CustomValuesForm from '../../farm-util/CustomValuesForm';

const lengthText = (type: IMusselLineResource['line_type']) =>
  type === 'LONGLINE' ? 'Harvested length' : 'Number of droppers';

interface IPickedItem {
  inventory_id: number;
  seeding_id: number;
  quantity: number | null;
}

interface InventoryProps {
  lineData: IMusselLineResource;
  form: IPickedItem[];
  setForm: (form: IPickedItem[]) => void;
}

const ReturningInventories = ({ lineData, form, setForm }: InventoryProps) => {
  const lang = useSelector(selectLang);
  const usedInventories = lineData.growing_cycle?.inventories_sum ?? [];

  const updateForm = (ivt_id: number, val: string) => {
    const ms = lineData.growing_cycle?.main_seed;
    if (!ms) return;

    const v = val.length <= 0 ? null : Number(val);
    let tmp = [...form];
    const i = tmp.findIndex(x => x.inventory_id === ivt_id);
    if (i < 0) {
      tmp.push({ inventory_id: ivt_id, seeding_id: ms.id, quantity: v });
    } else {
      tmp[i].quantity = v;
    }
    setForm(tmp);
  };

  return usedInventories.length > 0 ? (
    <div className='mt-17'>
      <Paragrapgh
        size={2}
        color='black'
        align='left'
        fontWeight={400}
        className='pb-12'
      >
        {translate(
          lang,
          'Please input quantity of inventories that are being harvested',
        )}
      </Paragrapgh>
      <div className='d-flex justify-between align-center'>
        {usedInventories.map(item => (
          <div className='mb-17' key={item.inventory_id}>
            <Input
              type='number'
              onChange={e => updateForm(item.inventory_id, e.target.value)}
              value={numToStr(
                form.find(x => x.inventory_id === item.inventory_id)?.quantity,
              )}
              label={`${item.inventory_name}`}
              placeholder={translate(
                lang,
                'Maximum value is %s',
                item.quantity,
              )}
              max={item.quantity}
            />
          </div>
        ))}
      </div>
    </div>
  ) : (
    <></>
  );
};

interface IHarvestGroup {
  batch_seed_id: number;
  amount?: number;
  line_length?: number;
}

interface ILineData {
  line_id: number;
  bags_quantity: number | null;
  is_full: boolean;
  is_final: boolean;
  line_length: number | null;
  inventories?: IPickedItem[];
  harvest_groups?: IHarvestGroup[];
  amount?: number | null;
  return_amount?: number | null;
  income?: number | null;
  shell_length?: number | null;
  shell_length_max?: number | null;
}

interface IBag {
  quantity?: number;
  capacity?: number;
}

interface IFormData {
  complete_date: number;
  shell_length: number | null;
  shell_length_max: number | null;
  harvest_number: string | null;
  port_of_unload: string | null;
  company: string | null;
  crop_owner: string | null;
  growing_area: string | null;
  delivered_to: string | null;
  packhouse: string | null;
  tag_color: string | null;
  shell_condition: string | null;
  harvester_name: string | null;
  mussel_type: string | null;
  comment: string | null;
  signature?: string | null;
  image_pdf_files?: IFileWall[];
  time_tracks?: ITimeRange[];
  boat_id?: number | null;
  income_details?: IExpenseRequest[];
  expenses?: IExpenseRequest[];
  harvest_bags?: IBag[];
  custom_values?: ICustomValue[];
}

const defaultForm: IFormData = {
  complete_date: moment().toDate().getTime(),
  shell_length: null,
  shell_length_max: null,
  harvest_number: null,
  port_of_unload: null,
  company: null,
  crop_owner: null,
  growing_area: null,
  delivered_to: null,
  packhouse: null,
  tag_color: null,
  shell_condition: null,
  harvester_name: null,
  mussel_type: null,
  comment: null,
  image_pdf_files: [],
  time_tracks: [],
  harvest_bags: [{ capacity: 500 }],
};

const getNewLineData = (
  lines: IMusselLineResource[],
  lineId: number,
): ILineData => {
  const line = lines.find(y => y.id === lineId);

  return {
    line_id: lineId,
    bags_quantity: null,
    is_full: true,
    is_final: true,
    line_length: line?.growing_cycle?.current_line_length ?? null,
    harvest_groups: undefined,
    amount: null,
    income: null,
  };
};

interface LineSectionProps {
  line: ILineData;
  allLines: IMusselLineResource[];
  lineOptions: Array<{
    id: string;
    value: string;
    label: string;
  }>;
  updateHarvestedLine: (lineId: number, key: string, value: any) => void;
  disabled?: boolean;
  rememberShellLength?: boolean;
}

const LineSection = ({
  line,
  allLines,
  lineOptions,
  updateHarvestedLine,
  disabled,
  rememberShellLength,
}: LineSectionProps) => {
  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const lineSrc = allLines.find(x => x.id === line.line_id)!;

  const isFull =
    !lineSrc.growing_cycle ||
    (line.line_length &&
      lineSrc.growing_cycle.current_line_length <= line.line_length);

  return (
    <div className='section' key={line.line_id}>
      <Subtitle color='black-2' size={0} fontWeight={600} align='left'>
        {translate(
          lang,
          'Detail of %s',
          lineOptions.find(x => Number(x.id) === line.line_id)?.label ?? '',
        )}
      </Subtitle>

      <div className='d-flex mb-17 mt-17'>
        <div className='flex-1 mr-12'>
          <ReturningInventories
            lineData={lineSrc}
            form={line.inventories ?? []}
            setForm={v => updateHarvestedLine(line.line_id, 'inventories', v)}
          />
          <div className='mb-17'>
            <Input
              type='number'
              label={translate(lang, lengthText(lineSrc.line_type))}
              value={numToStr(line.line_length)}
              onChange={e =>
                updateHarvestedLine(line.line_id, 'line_length', e.target.value)
              }
              unit={
                lineSrc.line_type === 'LONGLINE'
                  ? getUnitOfSpecie(unitType, 'distance_m')
                  : undefined
              }
            />
            <div className='color-red mt-3'>
              {translate(
                lang,
                isFull ? 'Full - will be empty' : 'Partial - Mussels remains',
              )}
            </div>
          </div>
        </div>
        <div className='flex-1 ml-12'>
          {rememberShellLength && (
            <div className='d-flex gap-12 mb-17'>
              <div className='flex-1'>
                <Input
                  type='number'
                  label={translate(lang, 'Shell length min')}
                  value={numToStr(line.shell_length)}
                  onChange={e =>
                    updateHarvestedLine(
                      line.line_id,
                      'shell_length',
                      e.target.value,
                    )
                  }
                  unit={getUnitOfSpecie(unitType, 'size_mm')}
                  disabled={disabled}
                />
              </div>
              <div className='flex-1'>
                <Input
                  type='number'
                  label={translate(lang, 'Shell length max')}
                  value={numToStr(line.shell_length_max)}
                  onChange={e =>
                    updateHarvestedLine(
                      line.line_id,
                      'shell_length_max',
                      e.target.value,
                    )
                  }
                  unit={getUnitOfSpecie(unitType, 'size_mm')}
                  disabled={disabled}
                />
              </div>
            </div>
          )}
          {!isFull && (lineSrc?.growing_cycle?.seedings.length ?? 0) > 0 && (
            <>
              <HarvestBatchSelect
                type='MUSSEL'
                growingSeeds={lineSrc?.growing_cycle?.seedings ?? []}
                values={line.harvest_groups?.map(x => x.batch_seed_id) ?? []}
                onChange={(sIds: number[] | undefined) =>
                  updateHarvestedLine(
                    line.line_id,
                    'harvest_groups',
                    sIds
                      ? sIds.map((id: number) => ({
                          batch_seed_id: id,
                          line_length: line.harvest_groups?.find(
                            y => y.batch_seed_id === id,
                          )?.line_length,
                        }))
                      : undefined,
                  )
                }
              />
              <HarvestGroupLensForm
                type='MUSSEL'
                lineType={lineSrc?.line_type}
                growingSeeds={lineSrc?.growing_cycle?.seedings ?? []}
                values={line.harvest_groups}
                onChange={vals =>
                  updateHarvestedLine(line.line_id, 'harvest_groups', vals)
                }
                className='mb-17 mt-17'
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface Props {
  visible: boolean;
  title?: string;
  data?: any;
  farm?: IFarmResource;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  overConfirm?: (data: any) => void;
  onlyView?: boolean;
  confirmID?: number;
  warningMessage?: string;
}

const MusselBulkHarvestModal = ({
  visible,
  title,
  farm: farmData,
  data: paramData,
  onCancel,
  onConfirm,
  overConfirm,
  onlyView,
  confirmID,
  warningMessage,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const farmsData = SelectFarmsByFarmID(farmData?.id);
  const farmUtils = SelectUtilsByFarmID(farmData?.id);
  const profile = useSelector(selectProfile);
  const account = useSelector(selectAccount);
  const unitType = account?.display_unit;
  const showBudget = SelectIsBudgetVisible(farmData?.id);
  const detailedBudgetEnabled = SelectDetailedBudgetEnabled(farmData?.id);
  const customFields = SelectHarvestFields(farmData?.id);

  const port_of_unloads = farmUtils.filter(x => x.type === 'port_of_unload'),
    delivered_tos = farmUtils.filter(x => x.type === 'delivered_to'),
    packhouses = farmUtils.filter(x => x.type === 'packhouse');

  const allLines = useMemo(() => {
    let result: IMusselLineResource[] = [];
    for (let farm of farmsData) {
      result = [
        ...result,
        ...(farm.lines.filter(
          x => x.crop_type === 'MUSSEL',
        ) as IMusselLineResource[]),
      ];
    }
    return result;
  }, [farmsData]);

  const linesOptions = allLines
    .filter(x => !!x.growing_cycle)
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: `${x.farm_name} (${x.farm_number}) - ${x.line_name}`,
    }));

  const signatureCanvas = useRef<SignatureCanvas | null>();

  const [totalAmount, setTotalAmount] = useState<number | null>(
    paramData?.lines
      ? paramData.lines.reduce(
          (sum: number, line: ILineData) => sum + Number(line.amount ?? 0),
          0,
        )
      : null,
  );
  const [totalBags, setTotalBags] = useState<number | null>(
    paramData?.lines
      ? paramData.lines.reduce(
          (sum: number, line: ILineData) =>
            sum + Number(line.bags_quantity ?? 0),
          0,
        )
      : null,
  );
  const [returnAmountSum, setReturnAmountSum] = useState<number | null>(
    paramData?.lines
      ? paramData.lines.reduce(
          (sum: number, line: ILineData) =>
            sum + Number(line.return_amount ?? 0),
          0,
        )
      : null,
  );
  const [isAllFinal, setIsAllFinal] = useState<boolean>(
    !paramData ||
      paramData.lines?.every((l: ILineData) => l.is_final) !== false,
  );

  const [formData, setFormData] = useState<IFormData>(
    paramData
      ? {
          ...defaultForm,
          ...paramData,
          complete_date: paramData.complete_date
            ? toMillisecond(paramData.complete_date)
            : Date.now(),
          time_tracks: paramData.time_tracks
            ? paramData.time_tracks.map((x: any) => ({
                start_time: toMillisecond(x.start_time),
                finish_time: toMillisecond(x.finish_time),
              }))
            : [],
          harvest_bags: paramData.harvest_bags ?? [{ capacity: 500 }],
        }
      : {
          ...defaultForm,
          port_of_unload:
            port_of_unloads && port_of_unloads.length > 0
              ? port_of_unloads[0].name
              : defaultForm.port_of_unload,
          delivered_to:
            delivered_tos && delivered_tos.length > 0
              ? delivered_tos[0].name
              : defaultForm.delivered_to,
          packhouse:
            packhouses && packhouses.length > 0
              ? packhouses[0].name
              : defaultForm.packhouse,
          crop_owner:
            farmData && farmData.owners && farmData.owners.length > 0
              ? farmData.owners[0].title
              : null,
          growing_area: farmData?.name,
          harvester_name: profile?.name ?? null,
          company: profile?.company_name ?? null,
          boat_id: profile?.current_boat?.id,
          income_details: detailedBudgetEnabled
            ? [{ name: '', quantity: null, unit_price: null, amount: null }]
            : undefined,
          expenses: detailedBudgetEnabled
            ? [{ name: '', quantity: null, unit_price: null, amount: null }]
            : undefined,
        },
  );
  const [harvestedLines, setHarvestedLines] = useState<ILineData[]>(
    paramData && paramData.lines ? paramData.lines : [],
  );
  const [harvestCosts, setHarvestCosts] = useState<IExpenseRequest[]>(
    paramData?.expenses?.map((x: any) => ({
      name: x.name,
      amount: x.amount,
      quantity: x.quantity,
      unit_price: x.quantity
        ? Math.round((x.amount / x.quantity) * 100) / 100
        : null,
    })) ?? [{ name: '', amount: null, quantity: null }],
  );
  const [harvestIncomes, setHarvestIncomes] = useState<IExpenseRequest[]>(
    paramData?.income_details?.map((x: any) => ({
      name: x.name,
      amount: x.amount,
      quantity: x.quantity,
      unit_price: x.quantity
        ? Math.round((x.amount / x.quantity) * 100) / 100
        : null,
    })) ?? [{ name: '', amount: null, quantity: null }],
  );
  const [rememberShellLength, setRememberShellLength] = useState<boolean>(
    !!paramData?.lines?.some((x: any) => !isNullOrUndefined(x.shell_length)),
  );
  const [disabled, setDisabled] = useState(false);
  const [sigCanLoaded, setSigCanLoaded] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  const updateHarvestedLine = (lineId: number, key: string, value: any) => {
    if (
      !['is_full', 'is_final', 'harvest_groups', 'inventories'].includes(key)
    ) {
      value = value.length <= 0 ? null : Number(value);
    }
    let tmp = [...harvestedLines];
    const i = harvestedLines.findIndex(x => x.line_id === lineId);
    if (i > -1) {
      (tmp[i] as any)[key] = value;
    }
    if (key === 'harvest_groups') {
      const lengthSum =
        value?.reduce(
          (sum: number, x: IHarvestGroup) => sum + (x.line_length ?? 0),
          0,
        ) ?? 0;
      tmp[i].line_length = lengthSum > 0 ? lengthSum : null;
    }
    setHarvestedLines(tmp);
  };
  const selectLines = (val: string[]) => {
    let tmp: ILineData[] = val.map(x => getNewLineData(allLines, Number(x)));
    for (let t of harvestedLines) {
      const i = tmp.findIndex(x => x.line_id === t.line_id);
      if (i > -1) {
        tmp[i] = t;
      }
    }
    setHarvestedLines(tmp);
  };
  const toggleShellLength = (c: boolean) => {
    setRememberShellLength(c);
    if (c) {
      setHarvestedLines(prv =>
        prv.map(line => ({
          ...line,
          shell_length: formData.shell_length,
          shell_length_max: formData.shell_length_max,
        })),
      );
      setFormData(prv => ({
        ...prv,
        shell_length: null,
        shell_length_max: null,
      }));
    } else {
      setHarvestedLines(prv =>
        prv.map(line => ({
          ...line,
          shell_length: undefined,
          shell_length_max: undefined,
        })),
      );
    }
  };
  const updateForm = (key: keyof IFormData, value: any) => {
    if (['shell_length', 'shell_length_max'].includes(key)) {
      value = value.length <= 0 ? null : Number(value);
    }
    setFormData(prv => ({ ...prv, [key]: value }));
  };
  const updateHarvestBags = (bags: IBag[]) => {
    setFormData(prv => ({ ...prv, harvest_bags: bags }));
    setTotalAmount(
      bags.reduce((p, c) => p + (c.quantity ?? 0) * (c.capacity ?? 0), 0),
    );
    setTotalBags(bags.reduce((p, c) => p + (c.quantity ?? 0), 0));
  };

  const validForm = () => {
    if (harvestedLines.length <= 0) {
      setErrorMsg(translate(lang, '_sel_harvested_line'));
      return null;
    }
    if (!totalAmount || Number(totalAmount) <= 0) {
      setErrorMsg(translate(lang, 'Please input valid total harvest amount'));
      return null;
    }
    if (!totalBags || Number(totalBags) <= 0) {
      setErrorMsg(translate(lang, 'Please input valid total number of bags'));
      return null;
    }
    for (let line of harvestedLines) {
      if (!line.line_length || Number(line.line_length) <= 0) {
        setErrorMsg(
          translate(lang, 'Please input valid line length for all lines'),
        );
        return null;
      }
    }
    if (!rememberShellLength) {
      if (!formData.shell_length) {
        setErrorMsg(translate(lang, '_input_shell_length'));
        return null;
      }
      if (
        formData.shell_length_max !== null &&
        formData.shell_length_max !== undefined &&
        (formData.shell_length === null ||
          formData.shell_length > formData.shell_length_max)
      ) {
        setErrorMsg(
          translate(
            lang,
            'Maximum shell length must be greater than minimum shell length',
          ),
        );
        return null;
      }
    } else {
      for (let line of harvestedLines) {
        if (!line.shell_length) {
          setErrorMsg(
            translate(lang, 'Please input shell length for all lines'),
          );
          return null;
        }
        if (
          !isNullOrUndefined(line.shell_length_max) &&
          (line.shell_length === null ||
            line.shell_length > line.shell_length_max!)
        ) {
          setErrorMsg(
            translate(
              lang,
              'Maximum shell length must be greater than minimum shell length for all lines',
            ),
          );
          return null;
        }
      }
    }
    for (const bag of formData.harvest_bags ?? []) {
      if (
        !bag.capacity ||
        Number(bag.capacity) <= 0 ||
        !bag.quantity ||
        Number(bag.quantity) <= 0
      ) {
        setErrorMsg(
          translate(lang, 'Please input valid bag quantity and capacity'),
        );
        return null;
      }
    }
    const lengthSum = harvestedLines.reduce(
      (sum, line) => sum + (line.line_length ?? 0),
      0,
    );
    if (lengthSum <= 0) {
      setErrorMsg(
        translate(lang, 'Total line length must be greater than zero'),
      );
      return null;
    }
    return {
      ...formData,
      shell_length: rememberShellLength ? undefined : formData.shell_length,
      shell_length_max: rememberShellLength
        ? undefined
        : formData.shell_length_max,
      lines: harvestedLines.map(x => {
        const line = allLines.find(l => l.id === x.line_id);
        const lengthOfLine = x.line_length ?? 1;
        const amountOfLine = (totalAmount * lengthOfLine) / lengthSum;
        const bagsQty = Math.round((totalBags * lengthOfLine) / lengthSum);
        const isFull =
          !line?.growing_cycle ||
          line.growing_cycle.current_line_length <= lengthOfLine;
        const returnAmountOfLine = isAllFinal
          ? ((returnAmountSum ?? 0) * lengthOfLine) / lengthSum
          : null;
        return {
          ...x,
          is_final: isAllFinal,
          is_full: isFull,
          amount: amountOfLine,
          return_amount: returnAmountOfLine,
          line_length: lengthOfLine,
          bags_quantity: bagsQty,
          shell_length: rememberShellLength ? x.shell_length : undefined,
          shell_length_max: rememberShellLength
            ? x.shell_length_max
            : undefined,
          harvest_groups: x.harvest_groups?.map((y, i) => ({
            batch_seed_id: y.batch_seed_id,
            amount: divideEqually(
              amountOfLine,
              x.harvest_groups?.length ?? 0,
              i,
            ),
            line_length:
              x.line_length ??
              divideEqually(lengthOfLine, x.harvest_groups?.length ?? 0, i),
          })),
        };
      }),
      expenses: detailedBudgetEnabled
        ? harvestCosts.filter(x => x.quantity && x.amount)
        : undefined,
      income_details: detailedBudgetEnabled
        ? harvestIncomes.filter(x => x.quantity && x.amount)
        : undefined,
      complete_date: toSecond(formData.complete_date),
      harvest_bags: formData.harvest_bags?.map(x => ({
        quantity: x.quantity,
        capacity: x.capacity,
      })),
      time_tracks: formData.time_tracks?.map(x => ({
        start_time: toSecond(x.start_time),
        finish_time: toSecond(x.finish_time),
      })),
      signature: signatureCanvas.current?.toDataURL(),
      custom_values: (() => {
        const cvs = formData.custom_values?.filter(x => !!x.field_val) ?? [];
        return cvs.length > 0 ? cvs : undefined;
      })(),
    };
  };

  const confirmClick = async () => {
    const data = validForm();
    if (!data) return;

    setDisabled(true);
    if (overConfirm) {
      overConfirm(data);
      return;
    }

    const res = confirmID
      ? await sendSingleRequest(
          { ...data, id: confirmID },
          'POST',
          'api/queue/confirm-bulk-harvest/mussel',
          true,
        )
      : await sendSingleRequest(
          data,
          'POST',
          'api/farm/line/bulk-mussel-harvest',
          true,
        );

    if (res.status) {
      setDisabled(false);
      dispatch(
        showFeedback({
          isMessage: true,
          message: translate(lang, res.data?.message ?? '_harvested_ok'),
          type: 'success',
        }),
      );
      dispatch(loadFarmsData());
      dispatch(loadQueueData());
      onConfirm(res.data);
    } else {
      setDisabled(false);
      setErrorMsg(translate(lang, res.data?.message ?? 'Failed to harvest'));
    }
  };

  useEffect(() => {
    if (paramData && sigCanLoaded && signatureCanvas.current) {
      if (paramData.signature) {
        signatureCanvas.current.fromDataURL(paramData.signature);
      }
    }
  }, [paramData, sigCanLoaded]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      maskClosable={false}
      closeIcon={<CloseIcon />}
      width={1000}
    >
      <div className='section-modal wrap'>
        {!!errorMsg && (
          <Feedback
            message={errorMsg}
            type={'error'}
            theme='light'
            position={10}
            isGlobal
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <div className='d-flex align-center mb-17'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {title ?? translate(lang, 'Bulk mussel harvest')}
          </Subtitle>
        </div>
        {!!warningMessage && (
          <div className='header-alert'>{warningMessage}</div>
        )}
        <div className='section pb-17'>
          <Dropdown
            className='mb-17'
            label={translate(lang, '_sel_harvested_lines')}
            placeholder={translate(lang, '_sel_harvested_lines')}
            mode='multiple'
            options={linesOptions}
            value={harvestedLines.map(x => x.line_id.toString()) as any}
            onChange={(v: any) => selectLines(v)}
            showSearch={true}
            disabled={onlyView}
          />
          <CheckboxButton
            label={translate(lang, 'Remember Shell Length')}
            checked={rememberShellLength}
            onChange={e => toggleShellLength(e.target.checked)}
          />
        </div>
        {harvestedLines.map((line, index) => (
          <LineSection
            key={line.line_id}
            line={line}
            allLines={allLines}
            lineOptions={linesOptions}
            updateHarvestedLine={updateHarvestedLine}
            disabled={onlyView}
            rememberShellLength={rememberShellLength}
          />
        ))}
        <div className='section'>
          <div className='item-row mb-17' style={{ alignItems: 'start' }}>
            <div className='item-cell'>
              <div className='mb-17'>
                <Datepicker
                  label={translate(lang, '_harvest_complete_date')}
                  defaultValue={formData.complete_date}
                  onChange={e =>
                    e && updateForm('complete_date', e.toDate().getTime())
                  }
                />
              </div>
              <div className='mb-17'>
                <Dropdown
                  label={translate(lang, 'Are all lines harvested as final?')}
                  value={isAllFinal ? 'yes' : 'no'}
                  options={[
                    {
                      id: 'yes',
                      value: 'yes',
                      label: translate(lang, 'Yes, this is final harvest'),
                    },
                    {
                      id: 'no',
                      value: 'no',
                      label: translate(lang, '_itm_harvest'),
                    },
                  ]}
                  onChange={v => setIsAllFinal(v === 'yes')}
                  disabled={onlyView}
                />
              </div>
              <div className='mb-17'>
                <Input
                  label={translate(lang, 'Harvest number')}
                  type='text'
                  value={formData.harvest_number ?? ''}
                  onChange={e => updateForm('harvest_number', e.target.value)}
                  disabled={onlyView}
                />
              </div>
              {!rememberShellLength && (
                <div className='d-flex gap-12'>
                  <div className='flex-1'>
                    <Input
                      type='number'
                      label={translate(lang, 'Shell length min')}
                      value={numToStr(formData.shell_length)}
                      onChange={e => updateForm('shell_length', e.target.value)}
                      unit={getUnitOfSpecie(unitType, 'size_mm')}
                    />
                  </div>
                  <div className='flex-1'>
                    <Input
                      type='number'
                      label={translate(lang, 'Shell length max')}
                      value={numToStr(formData.shell_length_max)}
                      onChange={e =>
                        updateForm('shell_length_max', e.target.value)
                      }
                      unit={getUnitOfSpecie(unitType, 'size_mm')}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className='item-cell'>
              <HarvestBagsForm
                data={formData.harvest_bags}
                onChange={updateHarvestBags}
                className='mb-17'
              />
              <div className='mb-17'>
                <Input
                  type='number'
                  label={translate(lang, 'Total Harvest Amount')}
                  value={numToStr(totalAmount)}
                  onChange={e =>
                    setTotalAmount(
                      e.target.value.length > 0 ? Number(e.target.value) : null,
                    )
                  }
                  unit={getUnitOfSpecie(unitType, 'weight_kg')}
                  disabled={onlyView}
                />
              </div>
              {isAllFinal && (
                <div className='mb-17'>
                  <Input
                    type='number'
                    label={translate(lang, 'Return amount back to storage')}
                    value={numToStr(returnAmountSum)}
                    onChange={e =>
                      setReturnAmountSum(
                        e.target.value.length > 0
                          ? Number(e.target.value)
                          : null,
                      )
                    }
                    unit={getUnitOfSpecie(unitType, 'weight_kg')}
                    disabled={onlyView}
                  />
                </div>
              )}
            </div>
          </div>
          {showBudget && detailedBudgetEnabled && (
            <>
              <ExpensesForm
                title={translate(lang, 'Harvest Income Details')}
                data={harvestIncomes}
                setData={setHarvestIncomes}
                hideTotal={true}
              />
              <ExpensesForm
                title={translate(lang, 'Harvest Expenses')}
                data={harvestCosts}
                setData={setHarvestCosts}
                hideTotal={true}
              />
            </>
          )}
        </div>
        <div className='section'>
          <MoreFieldsForm
            type='MUSSEL'
            farmID={farmData?.id}
            form={formData}
            onChange={(d: IFormData) => setFormData(d)}
            disabled={onlyView}
          />
          <CustomValuesForm
            values={formData.custom_values ?? []}
            fields={customFields}
            onChange={data =>
              setFormData(prv => ({ ...prv, custom_values: data }))
            }
            disabled={onlyView}
          />
          <div className='mb-17'>
            <Input
              label={translate(lang, 'Comment')}
              placeholder={translate(lang, 'Comment')}
              type='textarea'
              onChange={e => updateForm('comment', e.target.value)}
              value={formData.comment ?? ''}
              name='comment'
              disabled={onlyView}
            />
          </div>
          <div className='mb-17'>
            <FilesWall
              label={translate(lang, 'Images & PDF Files')}
              fileList={formData.image_pdf_files}
              onChange={v => updateForm('image_pdf_files', v)}
              disabled={onlyView}
            />
          </div>
          <div className='mb-20'>
            <p className='mb-4 d-block paragrapgh paragrapgh--2 paragrapgh--400 paragrapgh--default'>
              {translate(lang, 'Signature')}
            </p>
            <div
              style={{
                border: '1px solid #EFEFEF',
                borderRadius: '4px',
                height: '200px',
              }}
            >
              <SignatureCanvas
                ref={r => {
                  signatureCanvas.current = r;
                  setSigCanLoaded(true);
                }}
                penColor='green'
                canvasProps={{ width: 550, height: 200 }}
              />
            </div>
          </div>
        </div>
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width='small'
            className='rsp-btn'
            size={2}
            type='fill'
            color='red'
            onClick={onCancel}
            disabled={disabled}
          >
            {translate(lang, 'Cancel')}
          </Button>
          <Button
            width='small'
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={confirmClick}
            disabled={disabled || harvestedLines.length === 0 || onlyView}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MusselBulkHarvestModal;
